/**
 * Change your own password, as a section of the profile page.
 *
 * This used to be a dialog hanging off the sidebar menu. It lives here now
 * because a password is part of your account, not a floating action - and a
 * settings page is where people look for it when nobody has told them where it
 * is.
 *
 * It needs a connection. Unlike the catalog, a password is not something that
 * can be changed locally and reconciled later: the hash lives on the server and
 * a queued "change password" would be both useless and dangerous.
 */
import { useEffect, useState } from 'react';
import { Check, KeyRound } from 'lucide-react';
import { ApiClientError } from '@mouldtrack/shared';
import { Button } from '../../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/primitives.js';
import { Field, Input } from '../../components/ui/field.js';
import { changePassword, isNetworkError } from '../../api/client.js';
import { useOnlineStatus } from '../../sync/useSync.js';

const MIN_LENGTH = 8;

export function ChangePasswordCard() {
  const online = useOnlineStatus();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  // Clear the confirmation after a while so the card returns to its normal
  // state rather than looking permanently "just changed".
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 8000);
    return () => clearTimeout(t);
  }, [done]);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Checked here as well as on the server, so the common mistakes are caught
    // without a round trip.
    if (next.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    if (next === current) {
      setError('The new password is the same as your current one.');
      return;
    }

    setSaving(true);
    try {
      await changePassword({ currentPassword: current, newPassword: next });
      reset();
      setDone(true);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else if (isNetworkError(err)) setError('No connection. A password can only be changed online.');
      else setError('Could not change the password. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted-foreground" aria-hidden />
          Password
        </CardTitle>
        {done ? (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Check className="size-3.5" aria-hidden /> Changed
          </span>
        ) : null}
      </CardHeader>
      <CardBody>
        <form onSubmit={submit} className="flex max-w-md flex-col gap-4">
          {/* Helps password managers attach the change to the right account. */}
          <input type="text" autoComplete="username" className="hidden" tabIndex={-1} aria-hidden />

          <Field label="Current password" required>
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field label="New password" required hint={`At least ${MIN_LENGTH} characters.`}>
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </Field>
          <Field label="Confirm new password" required>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>

          {error ? (
            <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          {!online ? (
            <p className="rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning">
              You are offline. A password can only be changed while connected.
            </p>
          ) : null}

          <div className="flex items-center gap-2">
            <Button type="submit" variant="primary" loading={saving} disabled={!online}>
              Change password
            </Button>
            {current || next || confirm ? (
              <Button type="button" variant="ghost" onClick={reset} disabled={saving}>
                Clear
              </Button>
            ) : null}
          </div>

          <p className="text-xs text-muted-foreground">
            You stay signed in here, and so does every other device signed in as you. They will ask
            for the new password the next time they sign in.
          </p>
        </form>
      </CardBody>
    </Card>
  );
}
